import { API_URL } from '../utils/ConfigUtils';

export interface FileUploadResponse {
  success: boolean;
  message: string;
  file: {
    filename: string;
    stored_name: string;
    url: string;
    size_mb: number;
    type: string;
    uploaded_at: string;
  };
}

export interface FileInfo {
  filename: string;
  stored_name: string;
  url: string;
  size_mb: number;
  type: string;
  uploaded_at: string;
}

// File compression utility - ENHANCED for all file types
export const compressFile = async (file: File, maxSizeMB: number = 5): Promise<File> => {
  console.log(`Compressing file: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB, type: ${file.type}`);
  
  // If file is already small enough, return as is
  if (file.size <= maxSizeMB * 1024 * 1024) {
    console.log('File already small enough, no compression needed');
    return file;
  }

  // For images, we can compress them
  if (file.type.startsWith('image/')) {
    console.log('Compressing image file...');
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions while maintaining aspect ratio
        const maxDimension = 1200;
        let { width, height } = img;
        
        if (width > height && width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        } else if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx?.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) {
            const compressedFile = new File([blob], file.name, {
              type: file.type,
              lastModified: Date.now(),
            });
            console.log(`Image compressed: ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
            resolve(compressedFile);
          } else {
            console.log('Image compression failed, using original');
            resolve(file);
          }
        }, file.type, 0.8); // 80% quality
      };
      
      img.src = URL.createObjectURL(file);
    });
  }
  
  // For non-image files, try to split large files or provide better error handling
  console.log(`Non-image file, size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
  
  // If file is extremely large (>50MB), reject it
  if (file.size > 50 * 1024 * 1024) {
    throw new Error(`File is too large (${(file.size / 1024 / 1024).toFixed(1)}MB). Maximum allowed size is 50MB.`);
  }
  
  // For large non-image files, we can't compress them, but we can provide better feedback
  if (file.size > 25 * 1024 * 1024) {
    console.log('Large non-image file detected - will attempt upload but may be slow');
  }
  
  return file;
};

// File validation utility
export const validateFile = (file: File, maxSizeMB: number = 25): { isValid: boolean; error?: string } => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  
  if (file.size > maxSizeBytes) {
    return { 
      isValid: false, 
      error: `File size (${(file.size / (1024 * 1024)).toFixed(1)}MB) exceeds maximum allowed size of ${maxSizeMB}MB` 
    };
  }
  
  const allowedTypes = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/csv',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp'
  ];
  
  if (!allowedTypes.includes(file.type)) {
    return { 
      isValid: false, 
      error: `File type ${file.type} is not allowed. Please upload PDF, Excel, CSV, Word, or image files.` 
    };
  }
  
  return { isValid: true };
};

export const uploadFile = async (
  file: File, 
  onProgress?: (progress: number) => void
): Promise<FileUploadResponse> => {
  try {
    console.log(`Starting upload for file: ${file.name}, size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
    
    const formData = new FormData();
    formData.append('file', file);

    // Get JWT token for authentication
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {};
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Create AbortController for timeout handling
    const controller = new AbortController();
    
    // Dynamic timeout based on file size: 1 minute per MB, minimum 2 minutes, maximum 10 minutes
    const timeoutMinutes = Math.min(Math.max(Math.ceil(file.size / (1024 * 1024)), 2), 10);
    const timeoutMs = timeoutMinutes * 60 * 1000;
    
    console.log(`Setting upload timeout to ${timeoutMinutes} minutes for ${(file.size / 1024 / 1024).toFixed(2)}MB file`);
    
    const timeoutId = setTimeout(() => {
      console.log('Upload timeout reached, aborting...');
      controller.abort();
    }, timeoutMs);

    // Simulate progress for large files since we can't get real progress from fetch
    if (onProgress && file.size > 5 * 1024 * 1024) { // Only for files > 5MB
      let progress = 0;
      const progressInterval = setInterval(() => {
        if (progress < 90) { // Don't go to 100% until actually complete
          progress += Math.random() * 10;
          onProgress(Math.min(progress, 90));
        }
      }, 1000);
      
      // Clean up progress interval
      setTimeout(() => clearInterval(progressInterval), timeoutMs - 1000);
    }

    const response = await fetch(`${API_URL}/files/upload`, {
      method: 'POST',
      body: formData,
      headers,
      signal: controller.signal,
      // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
    });

    clearTimeout(timeoutId);
    
    if (onProgress) {
      onProgress(100); // Upload complete
    }

    console.log(`Upload response status: ${response.status}`);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `Upload failed with status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Upload successful:', data);
    return data;
  } catch (error) {
    console.error('File upload error:', error);
    if (error instanceof Error && error.name === 'AbortError') {
      const timeoutMinutes = Math.min(Math.max(Math.ceil(file.size / (1024 * 1024)), 2), 10);
      throw new Error(`Upload timed out after ${timeoutMinutes} minutes. Please try again with a smaller file or check your connection.`);
    }
    throw error;
  }
};

export const deleteFile = async (filename: string): Promise<boolean> => {
  try {
    // Get JWT token for authentication
    const token = localStorage.getItem('token');
    const headers: HeadersInit = {};
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${API_URL}/files/delete/${filename}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Delete failed with status: ${response.status}`);
    }

    return true;
  } catch (error) {
    console.error('File deletion error:', error);
    throw error;
  }
};

export const getFileUrl = (filename: string): string => {
  return `${API_URL}/files/app/uploads/${filename}`;
};
